import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Col, Container, Row } from "reactstrap";
import { useIntl } from "react-intl";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import { useGlobalCore } from "../../cores/globals";
import { EVENT_CREATION_LOADING } from "../../constants/loading.constant";
import { useWebFormCore } from "../../cores/webForm";
import { Action } from "../../cores/webForm/constants/Action.constant";
import { FormInput } from "../../cores/webForm/interfaces/api/post-public-form";
import FormFieldsView from "../component/forms/web/FormFieldsView";
import {
  collectFormInputErrors,
  mergeFormInputErrors,
} from "../../cores/webForm/utils/formRender.utils";

export const WebForm = () => {
  const intl = useIntl();
  const { company, form } = useParams<{ company: string; form: string }>();
  const { isLoading } = useGlobalCore();
  const { getPublicWebForm, postPublicWebForm, webForm } = useWebFormCore();
  const [inputs, setInputs] = useState<FormInput[]>([]);
  const [inputErrors, setInputErrors] = useState<string[]>([]);
  const [companyId, setCompanyId] = useState("");
  const [showSuccessfulMessage, setShowSuccessfulMessage] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (company && form) {
      const decodedCompanyId = atob(company);
      const formId = atob(form);
      setCompanyId(decodedCompanyId);
      setLoadError(null);
      getPublicWebForm(decodedCompanyId, formId).then((loaded) => {
        if (!loaded) {
          setLoadError("Este formulario no está disponible.");
        }
      });
    }
  }, [company, form, getPublicWebForm]);

  useEffect(() => {
    if (!webForm?.inputs) return;
    setInputs(
      webForm.inputs.map((input) => ({
        ...input,
        value: "",
      }))
    );
  }, [webForm?.inputs, webForm?.form?.design]);

  const resetForm = useCallback(() => {
    setInputErrors([]);
    setInputs(
      webForm.inputs.map((input) => ({
        ...input,
        value: "",
      }))
    );
  }, [webForm.inputs]);

  const onFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const errors = collectFormInputErrors(inputs);
      setInputErrors(errors);

      if (errors.length > 0 || !webForm.form) return;

      postPublicWebForm(
        companyId,
        {
          companyId,
          formId: webForm.form.id,
          form: inputs,
          reference: window.location.href,
        },
        () => {
          if (webForm.form?.action === Action.MESSAGE) {
            setShowSuccessfulMessage(true);
            resetForm();
            setTimeout(() => {
              setShowSuccessfulMessage(false);
            }, 2000);
          } else {
            window.open(webForm.form?.redirectUrl, "_self");
          }
        }
      );
    },
    [companyId, inputs, webForm, postPublicWebForm, resetForm]
  );

  const onChangingInput = useCallback(
    (input: FormInput, value: string) => {
      const copyInputs = [...inputs];
      const inputIndex = inputs.findIndex(
        (inpt) => String(inpt.id) === String(input.id)
      );
      if (inputIndex >= 0) {
        copyInputs[inputIndex].value = value;
        setInputs(copyInputs);
        setInputErrors((current) =>
          mergeFormInputErrors(
            current,
            input.id,
            collectFormInputErrors([{ ...copyInputs[inputIndex] }]).includes(input.id)
          )
        );
      }
    },
    [inputs]
  );

  if (loadError) {
    return (
      <Container className="h-100 w-100 overflow-auto" fluid>
        <Row className="h-100">
          <Col md={{ offset: 4, size: 4 }} sm="12">
            <Alert color="warning" className="my-5 text-center">
              {loadError}
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  if (!webForm.form) {
    return null;
  }

  return (
    <Container className="h-100 w-100 overflow-auto bg-light" fluid>
      <Row className="h-100">
        <Col md={{ offset: 4, size: 4 }} sm="12">
          <FormFieldsView
            form={webForm.form}
            inputs={inputs}
            inputErrors={inputErrors}
            showSuccessfulMessage={showSuccessfulMessage}
            submitting={isLoading(EVENT_CREATION_LOADING)}
            columnLayout
            className="h-100 d-flex flex-column align-items-center justify-content-center overflow-auto p-3"
            innerClassName="w-100 p-4 my-3 shadow-sm bg-white rounded"
            onInputChange={onChangingInput}
            onSubmit={onFormSubmit}
          />
          <UpdateLoadingCard
            loadingId={EVENT_CREATION_LOADING}
            text={intl.formatMessage({ id: "app.meetings.creation" })}
          />
        </Col>
      </Row>
    </Container>
  );
};
